import { useCallback, useRef } from "react";
import { doc, onSnapshot, runTransaction, serverTimestamp } from "firebase/firestore";
import type { Firestore } from "firebase/firestore";

type Options = {
  db: Firestore;
  lockMs: number;
  currentUserString: () => string | null;
  setEditIsReadOnly: (val: boolean) => void;
  onDocUpdate?: (id: string, data: any) => void;
};

const MAX_HEARTBEAT_FAILS = 3;

export function useItemLock({ db, lockMs, currentUserString, setEditIsReadOnly, onDocUpdate }: Options) {
  const lockHeartbeatRef = useRef<number | null>(null);
  const lockWatcherUnsub = useRef<null | (() => void)>(null);
  const heartbeatFailCountRef = useRef(0);

  const stopLockHeartbeat = useCallback(() => {
    if (lockHeartbeatRef.current != null) {
      clearInterval(lockHeartbeatRef.current);
      lockHeartbeatRef.current = null;
    }
  }, []);

  const startLockHeartbeat = useCallback(
    (itemId: string) => {
      stopLockHeartbeat();
      heartbeatFailCountRef.current = 0;
      lockHeartbeatRef.current = window.setInterval(async () => {
        try {
          await runTransaction(db, async (tx) => {
            const r = doc(db, "itInventory", itemId);
            const s = await tx.get(r);
            if (!s.exists()) return;
            const cur = s.data() as any;
            if (cur.lockedBy === currentUserString()) {
              tx.update(r, { lockedAt: serverTimestamp() } as any);
            }
          });
          // Om anropet lyckas, nollställ räknaren
          heartbeatFailCountRef.current = 0;
        } catch {
          // Om anropet misslyckas, öka räknaren
          heartbeatFailCountRef.current++;
          if (heartbeatFailCountRef.current >= MAX_HEARTBEAT_FAILS) {
            stopLockHeartbeat();
            setEditIsReadOnly(true);
            alert(
              "Anslutningen till databasen har brutits. För att förhindra dataförlust är fönstret nu skrivskyddat. Kontrollera din anslutning och ladda om."
            );
          }
        }
      }, lockMs);
    },
    [db, lockMs, currentUserString, stopLockHeartbeat, setEditIsReadOnly]
  );

  const stopLockWatcher = useCallback(() => {
    if (lockWatcherUnsub.current) {
      lockWatcherUnsub.current();
      lockWatcherUnsub.current = null;
    }
  }, []);

  const startLockWatcher = useCallback(
    (itemId: string, editIsReadOnly: boolean) => {
      stopLockWatcher();
      const r = doc(db, "itInventory", itemId);
      lockWatcherUnsub.current = onSnapshot(r, (snap) => {
        if (!snap.exists()) return;
        const cur = snap.data() as any;
        onDocUpdate?.(itemId, cur);
        const me = currentUserString?.() ?? null;
        const takenByOther = !!cur.lockedBy && cur.lockedBy !== me;
        const nowInvoiced = !!cur.invoiceReportId;
        if ((takenByOther || nowInvoiced) && !editIsReadOnly) {
          stopLockHeartbeat();
          setEditIsReadOnly(true);
          if (nowInvoiced) {
            alert("Posten låstes för fakturering under tiden. Fönstret är nu skrivskyddat.");
          } else {
            alert(`Låset togs över av ${cur.lockedBy}. Fönstret är nu skrivskyddat.`);
          }
        }
      });
    },
    [currentUserString, setEditIsReadOnly, stopLockHeartbeat, stopLockWatcher, db, onDocUpdate]
  );

  return {
    startLockHeartbeat,
    stopLockHeartbeat,
    startLockWatcher,
    stopLockWatcher,
  };
}
